<script>
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import TrackingCategorySlots from '../../components/settings/TrackingCategorySlots.svelte';
  import { settingsStore } from '../../stores/settingsStore.js';
  import { loadJobCustomFields } from '../../lib/settings/jobCustomFields.js';
  import { legacyFromSlot1 } from '../../lib/trackingSlots.js';

  const {
    invoiceTrackingSettings,
    invoiceCostFeeSettings,
    trackingCategories,
    loadingTrackingCategories,
    trackingCategoriesError,
    error,
    success,
    managedByOwner,
    saveInvoiceTrackingSettings,
    loadTrackingCategories,
    performSaveWithToast
  } = settingsStore;

  let jobCustomFields = [];
  let loadingCustomFields = false;
  let customFieldsError = null;

  function onInvoiceSlotsChange(event) {
    const tracking = event.detail;
    invoiceTrackingSettings.update((current) => ({
      ...current,
      ...legacyFromSlot1(tracking, 'jtJobField'),
      tracking
    }));
  }

  onMount(async () => {
    loadingCustomFields = true;
    customFieldsError = null;
    try {
      jobCustomFields = await loadJobCustomFields();
    } catch (err) {
      customFieldsError = err.message || 'Failed to load custom fields';
      jobCustomFields = [];
    } finally {
      loadingCustomFields = false;
    }
    try {
      await loadTrackingCategories();
    } catch {
      // The slot component shows trackingCategoriesError.
    }
  });
</script>

<SettingsLayout activeTab="invoice-settings" tabTitle="Invoice Settings">
  <fieldset class="contents" disabled={$managedByOwner}>
    <div>
      <h4 class="text-base font-semibold text-gray-900 mb-4">Advanced Invoice Settings</h4>
      <div class="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
        <div class="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div class="text-sm text-blue-800">
            <p><strong>Invoice Tracking Category:</strong></p>
            <p class="mt-1">When enabled, the selected JobTread job field is written into a Xero tracking category on each invoice line. A second category can carry another job field, for example Division.</p>
          </div>
        </div>

        <TrackingCategorySlots
          slots={$invoiceTrackingSettings.tracking}
          {jobCustomFields}
          {loadingCustomFields}
          {customFieldsError}
          categories={$trackingCategories}
          loadingCategories={$loadingTrackingCategories}
          categoriesError={$trackingCategoriesError}
          idPrefix="invoice"
          on:change={onInvoiceSlotsChange}
          on:save={saveInvoiceTrackingSettings}
          on:loadCategories={loadTrackingCategories}
        />

        {#if $success && $success.includes('Invoice tracking')}
          <div class="p-3 bg-green-50 border border-green-300 rounded-md">
            <p class="text-sm text-green-800">{$success}</p>
          </div>
        {/if}

        {#if $error && $error.startsWith('[INVOICE]')}
          <div class="p-3 bg-red-50 border border-red-300 rounded-md">
            <p class="text-sm text-red-800">❌ <strong>Error:</strong> {$error.replace('[INVOICE] ', '')}</p>
          </div>
        {/if}

        <div class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-md">
          <div class="pr-4">
            <p class="text-sm font-medium text-gray-700">Sync invoices to Xero as Draft</p>
            <p class="text-xs text-gray-500 mt-1">
              Invoices in Jobtread need to be 'Approved' to be eligble to be Synced to Xero. Regardless, on sync to Xero set the Status to Draft with this setting
            </p>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <span class="text-sm font-bold {$invoiceTrackingSettings.syncAsDraft ? 'text-green-600' : 'text-red-600'}">
              {$invoiceTrackingSettings.syncAsDraft ? 'ON' : 'OFF'}
            </span>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                bind:checked={$invoiceTrackingSettings.syncAsDraft}
                on:change={() => performSaveWithToast('✅ Sync invoices to Xero as Draft setting updated!')}
                class="sr-only peer"
              />
              <div class="w-14 h-7 bg-red-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>

        <div class="p-4 bg-white border border-gray-200 rounded-md space-y-2">
          <label for="lineItemRoundingMode" class="block text-sm font-medium text-gray-700">Rounding Behaviour</label>
          <select
            id="lineItemRoundingMode"
            bind:value={$invoiceTrackingSettings.lineItemRoundingMode}
            on:change={() => performSaveWithToast('✅ Rounding behaviour updated!')}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="preserve">Send quantities and unit prices as per JobTread</option>
            <option value="summarise">Summarise and simplify line items</option>
          </select>
          {#if $invoiceTrackingSettings.lineItemRoundingMode === 'summarise'}
            <p class="text-xs text-gray-500">
              Each line is sent as 1 × the JobTread line total (e.g. Qty 4 × Unit $10.00 = $40.00 becomes Qty 1 × Unit $40.00 = $40.00). Avoids rounding adjustment lines in most cases.
            </p>
          {:else}
            <p class="text-xs text-gray-500">
              Quantities and unit prices match JobTread. Small total differences may add a Rounding Adjustment line item.
            </p>
          {/if}
        </div>
      </div>
    </div>

    <!-- Cost & Fee (cost-plus) invoices -->
    <div class="mt-6">
      <h4 class="text-base font-semibold text-gray-900 mb-4">Cost &amp; Fee (Cost-Plus) Invoices</h4>
      <div class="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-4">
        <div class="p-3 bg-indigo-50 border border-indigo-200 rounded-md">
          <div class="text-sm text-indigo-800">
            <p><strong>Mirror JobTread's "Show Cost and Fee" presentation into Xero.</strong></p>
            <p class="mt-1">When a JobTread customer invoice has <em>Show Cost and Fee</em> turned on (its <code>showProfit</code> flag), the Xero invoice is built with each line at <strong>cost</strong> plus a single <strong>Fee</strong> line for the total markup — matching what the customer was billed. The total and GST are identical either way. Invoices without Show Cost and Fee are unaffected.</p>
          </div>
        </div>

        <div class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-md">
          <div class="pr-4">
            <p class="text-sm font-medium text-gray-700">Enable Cost &amp; Fee invoices</p>
            <p class="text-xs text-gray-500 mt-1">Off = today's behaviour (line items sent at marked-up price).</p>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              bind:checked={$invoiceCostFeeSettings.enabled}
              on:change={() => performSaveWithToast('✅ Cost & Fee setting updated!')}
              class="sr-only peer"
            />
            <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div>
          <label for="feeLabel" class="block text-sm font-medium text-gray-700 mb-2">Fee line label</label>
          <input
            type="text"
            id="feeLabel"
            bind:value={$invoiceCostFeeSettings.feeLabel}
            on:change={() => performSaveWithToast('✅ Fee label saved!')}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Builder's Fee"
          />
          <p class="mt-1 text-xs text-gray-500">The description shown on the single Fee line in Xero.</p>
        </div>

        <div>
          <label for="feeAccountCode" class="block text-sm font-medium text-gray-700 mb-2">Fee revenue account code (optional)</label>
          <input
            type="text"
            id="feeAccountCode"
            bind:value={$invoiceCostFeeSettings.feeAccountCode}
            on:change={() => performSaveWithToast('✅ Fee account code saved!')}
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="e.g., 200 (defaults to your invoice account code)"
          />
          <p class="mt-1 text-xs text-gray-500">The Xero account the Fee line posts to. Leave blank to use your default invoice account code.</p>
        </div>
      </div>
    </div>
  </fieldset>
</SettingsLayout>
